export const formatErrorInfo = (error, additionalInfo = {}) => {
  const baseInfo = {
    message: error?.message || "Unknown error",
    name: error?.name || "Error",
    stack: error?.stack,
    timestamp: new Date().toISOString(),
    url: window.location.href,
    userAgent: navigator.userAgent,
  };

  if (error?.status) {
    baseInfo.status = error.status;
    baseInfo.statusText = error.statusText;
  }

  if (error?.response?.data) {
    baseInfo.responseData = error.response.data;
  }

  return { ...baseInfo, ...additionalInfo };
};

export const extractValidationErrorsMessage = (errors) => {
  if (!errors || typeof errors !== "object" || Array.isArray(errors)) {
    return null;
  }

  const messages = Object.values(errors)
    .flatMap((fieldErrors) =>
      Array.isArray(fieldErrors) ? fieldErrors : [fieldErrors]
    )
    .filter((message) => typeof message === "string" && message.trim());

  if (messages.length === 0) {
    return null;
  }

  return messages.join(" ");
};

export const extractApiErrorMessage = (
  err,
  fallback = "An unexpected error occurred. Please try again."
) => {
  if (typeof err === "string" && err.trim()) {
    return err;
  }

  if (typeof err?.response?.data === "string" && err.response.data.trim()) {
    return err.response.data;
  }

  if (err?.response?.data && typeof err.response.data === "object") {
    const validationMessage = extractValidationErrorsMessage(
      err.response.data.errors
    );
    if (validationMessage) {
      return validationMessage;
    }

    const objectMessage = [
      err.response.data.message,
      err.response.data.Message,
      err.response.data.title,
      err.response.data.error,
    ].find((value) => typeof value === "string" && value.trim());

    if (objectMessage) {
      return objectMessage;
    }
  }

  if (typeof err?.message === "string" && err.message.trim()) {
    return err.message;
  }

  return fallback;
};

export const getUserFriendlyMessage = (error) => {
  if (error?.status === 404) {
    return "The requested resource was not found.";
  }

  if (error?.status === 403) {
    return "You do not have permission to access this resource.";
  }

  if (error?.status === 401) {
    return "Please log in to access this resource.";
  }

  if (error?.status >= 500) {
    return "A server error occurred. Please try again later.";
  }

  if (error?.status >= 400) {
    return "There was an issue with your request. Please check your input and try again.";
  }

  if (error?.message?.includes("Network Error")) {
    return "Unable to connect to the server. Please check your internet connection.";
  }

  if (error?.message?.includes("timeout")) {
    return "The request timed out. Please try again.";
  }

  return "An unexpected error occurred. Please try again or contact Sam if the problem persists.";
};

export const getErrorSeverity = (error) => {
  if (error?.status >= 500) {
    return "error";
  }

  if (error?.status >= 400) {
    return "warning";
  }

  return "error";
};

export const copyErrorToClipboard = async (error, additionalInfo = {}) => {
  try {
    const errorInfo = formatErrorInfo(error, additionalInfo);
    const errorText = JSON.stringify(errorInfo, null, 2);

    await navigator.clipboard.writeText(errorText);
    return true;
  } catch (clipboardError) {
    console.error("Failed to copy error to clipboard:", clipboardError);
    return false;
  }
};
